import React from 'react';
import { TEXT, TEXT2, TEXT3, BLUE, SUCCESS, WARNING, DANGER, BORDER, FONT } from '../../shared';

function ProgressBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ height: 5, borderRadius: 999, background: 'rgba(180,205,235,0.35)', overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${pct}%`, borderRadius: 999,
        background: `linear-gradient(90deg, ${color}cc, ${color})`,
        transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
      }} />
    </div>
  );
}

const STATUS_CONFIG = {
  active:   { color: SUCCESS,  label: 'Active'   },
  paused:   { color: WARNING,  label: 'Paused'   },
  ended:    { color: TEXT3,    label: 'Ended'    },
  draft:    { color: TEXT3,    label: 'Draft'    },
  learning: { color: BLUE,     label: 'Learning' },
};

export default function CampaignWidget({ data, onAction }) {
  if (!data) return null;

  const status = STATUS_CONFIG[data.status?.toLowerCase()] || STATUS_CONFIG.active;
  const spentPct = data.budget && data.spent != null ? Math.min(100, (Number(data.spent) / Number(data.budget)) * 100) : null;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.55)',
      backdropFilter: 'blur(22px)',
      WebkitBackdropFilter: 'blur(22px)',
      border: '1px solid rgba(255,255,255,0.60)',
      borderRadius: 18,
      padding: '16px 18px',
      boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 10px 28px -12px rgba(11,107,211,0.16)',
      fontFamily: FONT,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
        <span style={{ fontSize: 12, fontWeight: 700, color: TEXT, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
          {data.name || 'Campaign'}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700, color: status.color,
          background: `${status.color}14`, border: `1px solid ${status.color}30`,
          borderRadius: 999, padding: '2px 8px',
        }}>
          {status.label}
        </span>
      </div>

      {/* Metrics grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10, marginBottom: 14 }}>
        {[
          { label: 'Budget', value: data.budget ? `$${Number(data.budget).toLocaleString()}` : null },
          { label: 'Spent', value: data.spent != null ? `$${Number(data.spent).toLocaleString()}` : null },
          { label: 'Reach', value: data.reach != null ? Number(data.reach).toLocaleString() : null },
          { label: 'Clicks', value: data.clicks != null ? Number(data.clicks).toLocaleString() : null },
          { label: 'CTR', value: data.ctr != null ? `${data.ctr}%` : null },
          { label: 'ROAS', value: data.roas != null ? `${data.roas}x` : null },
        ].filter(m => m.value !== null).map((m, i) => (
          <div key={i} style={{
            background: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.65)',
            borderRadius: 10, padding: '10px 12px',
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
              {m.label}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: TEXT, letterSpacing: '-0.02em' }}>
              {m.value}
            </div>
          </div>
        ))}
      </div>

      {/* Budget progress */}
      {spentPct !== null && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: TEXT3 }}>Budget used</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: spentPct > 90 ? DANGER : spentPct > 70 ? WARNING : TEXT2 }}>
              {spentPct.toFixed(0)}%
            </span>
          </div>
          <ProgressBar value={Number(data.spent)} max={Number(data.budget)} color={spentPct > 90 ? DANGER : BLUE} />
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
        {data.status === 'active' && (
          <button
            onClick={() => onAction?.({ type: 'send_message', text: `Pause the ${data.name || 'campaign'} campaign.` })}
            style={{ padding: '7px 14px', borderRadius: 9, border: `1px solid ${WARNING}40`, background: `${WARNING}10`, color: WARNING, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
          >
            Pause
          </button>
        )}
        {data.status === 'paused' && (
          <button
            onClick={() => onAction?.({ type: 'send_message', text: `Resume the ${data.name || 'campaign'} campaign.` })}
            style={{ padding: '7px 14px', borderRadius: 9, border: `1px solid ${SUCCESS}40`, background: `${SUCCESS}10`, color: SUCCESS, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
          >
            Resume
          </button>
        )}
        <button
          onClick={() => onAction?.({ type: 'send_message', text: `Increase the budget for the ${data.name || 'campaign'} campaign by 20%.` })}
          style={{ padding: '7px 14px', borderRadius: 9, border: '1px solid rgba(14,165,233,0.3)', background: 'rgba(14,165,233,0.08)', color: BLUE, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
        >
          Increase budget
        </button>
        <button
          onClick={() => onAction?.({ type: 'send_message', text: `Show me the full performance report for the ${data.name || 'campaign'} campaign.` })}
          style={{ padding: '7px 14px', borderRadius: 9, border: '1px solid rgba(180,205,235,0.6)', background: 'rgba(255,255,255,0.55)', color: TEXT2, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: FONT }}
        >
          Full report
        </button>
      </div>
    </div>
  );
}
